import axios, { type AxiosError, type AxiosResponse } from 'axios';
import { API_URL } from '@/utils/helpers';

const http = axios.create({
  baseURL: API_URL,
});

type Success<T> = {
  data: T;
  response: AxiosResponse<T>;
  error: null;
};

type Failure<TError = unknown> = {
  data: null;
  response: AxiosResponse<TError> | undefined;
  error: AxiosError<TError>;
};

type Result<T, TError = unknown> = Success<T> | Failure<TError>;

/**
 * Wraps an Axios HTTP request to return a discriminated union result.
 * Automatically unwraps AxiosResponse objects to extract the data.
 * @param promise - The Axios promise to wrap
 * @returns A result object with data, response, and error
 * @example
 * const { data, response, error } = await tryHttp(http.get<MyType>('/api/endpoint'));
 *
 * if (error) {
 *   // Handle error, check response?.status if needed
 *   // error.response.data is typed as TError
 * } else {
 *   // Use data (already unwrapped from AxiosResponse)
 *   // Access response.status, response.headers, etc. if needed
 * }
 *
 * @example With error type
 * const { data, error } = await tryHttp<SuccessType, ErrorType>(http.post('/endpoint'));
 * if (error) {
 *   // error.response.data is now typed as ErrorType
 *   console.log(error.response?.data.message);
 * }
 */
const tryHttp = async <T, TError = unknown>(promise: Promise<AxiosResponse<T>>): Promise<Result<T, TError>> => {
  try {
    const response = await promise;
    return { data: response.data, response, error: null };
  } catch (error) {
    const axiosError = error as AxiosError<TError>;
    return { data: null, response: axiosError.response, error: axiosError };
  }
};

export { http, tryHttp };
